import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/happy-family-silhouette-sunset_1303-22466.jpg';

function FamilyCommunityHealth() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match reference layout used on other pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Who Can Apply for Health Coverage',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Families & individuals:', 'Apply for anyone in your household who needs coverage (including children).'],
            ['Immigrant families:', 'You can apply for your child even if you aren’t eligible; applying won’t affect immigration status.'],
            ['Programs included:', 'GTSCare and CoverKids; help paying Medicare costs may also be available.'],
          ].map(([k, v], i) => (
            <li key={`who-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'What You’ll Need to Apply',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['IDs & numbers:', 'Social Security Numbers (if available) or document numbers for legal immigrants who need insurance.'],
            ['Income details:', 'Recent paystubs, W-2s, or other income proofs for everyone in your family.'],
            ['Current coverage info:', 'Policy numbers for any health insurance you have now (other than GTSCare/CoverKids).'],
            ['Job-related coverage:', 'Information about any employer-sponsored insurance available to your family.'],
          ].map(([k, v], i) => (
            <li key={`need-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Ways to Apply',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Online:', 'Apply faster at gtscareconnect.gts.gov (create or log into your account).'],
            ['By phone:', 'Call GTSCare Connect at 855-259-0701 (Spanish help: 855-259-1234).'],
            ['By mail or fax:', 'Mail the completed application to GTSCare Connect, P.O. Box 305240, Washington, GT 12345, or fax to 855-315-0669.'],
            ['In person:', 'Apply at your local DHS office—find locations at gts.gov/humanservices.'],
          ].map(([k, v], i) => (
            <li key={`ways-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'After You Apply',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['If info is missing:', 'You’ll get a letter telling you what’s still needed and a cover page to return with your documents.'],
            ['Decision notice:', 'You’ll receive a written decision after your application and verifications are reviewed.'],
            ['Report changes:', 'Report changes within 10 days—online, by phone, mail, fax, or at a DHS office.'],
          ].map(([k, v], i) => (
            <li key={`after-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Authorized Representative',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['What they can do:', 'With your permission, they can sign and submit your application/renewal and receive copies of letters.'],
            ['Who can serve:', 'An individual or organization (e.g., hospital). Proof of legal representation can be submitted if applicable.'],
            ['Change at any time:', 'You can change or end representative rights by notifying GTSCare Connect.'],
          ].map(([k, v], i) => (
            <li key={`auth-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Help & Accessibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Phone assistance:', 'Call GTSCare Connect at 855-259-0701.'],
            ['In-person help:', 'Local DHS offices can assist with applications and documents.'],
            ['Additional supports:', 'Area Agency on Aging & Disability: 1-866-836-6678; DDA regional help lines available.'],
            ['Language access:', 'Interpreter services and translated materials available.'],
          ].map(([k, v], i) => (
            <li key={`help-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Privacy, Rights, and Non-Discrimination',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Privacy:', 'Your information is used only to determine eligibility and is kept private as required by law.'],
            ['Non-discrimination:', 'We do not discriminate based on race, color, national origin, age, sex, disability, religion, or language.'],
            ['Appeals:', 'You can appeal eligibility decisions; instructions are provided in decision letters.'],
          ].map(([k, v], i) => (
            <li key={`rights-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Renewals & Communication Preferences',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Annual renewals:', 'Eligibility is usually renewed each year; you can allow use of tax data to streamline renewal.'],
            ['Letters:', 'Choose mail or electronic letters in your online account; update preferences anytime.'],
          ].map(([k, v], i) => (
            <li key={`renew-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Voter Registration Information',
      Answer: (
        <p style={valText}>
          GTSCare is a voter registration agency. You may request a voter registration form. Applying to register or choosing not to
          register does not affect your eligibility or benefits.
        </p>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Family and Community Health Programs" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Family and Community Health Programs</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We support families with maternal and child health, preventive care, and access to coverage—so households get the
                right care at the right time with clear steps to apply, verify, and keep benefits active.
              </p>

              {/* Download button BELOW overview, ABOVE other sections */}
              {/* <div style={{ margin: '0 0 16px' }}>
                <a href="/guides/GTSCarePaperApp.pdf" className="btn btn-primary" download>
                  Download Health Care Application (PDF)
                </a>
                <Link to="/forms" className="btn btn-secondary">
                  Apply for a Permit (Online Forms)
                </Link>
              </div> */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0 0 16px' }}>
                <a
                  href="/guides/GTSCarePaperApp.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Health Care Application (PDF)
                </a>
                <Link
                  to="/forms"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#666',
                    textDecoration: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Apply for Support (Online Forms)
                </Link>
              </div>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We are committed to improving the health and well-being of families and communities by delivering comprehensive,
                accessible services and clear navigation to benefits.
              </p>
            </div>

            <div style={{ marginTop: 12 }}>
              <Link to="/department/health">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default FamilyCommunityHealth;
